import { useEffect, useRef, useState } from 'react'
import { calculatePosition, calculateTime } from './utils'

type Props = {
  start: Date
  end: Date
  step?: number
  value: Date
  onChange: (value: Date) => void
  dir?: 'ltr' | 'rtl'
}

export const useTimeAdjustment = ({
  step = 5,
  start,
  end,
  onChange,
  dir = 'ltr',
}: Props) => {
  const startTime = new Date(start.getTime() + 30 * 60 * 1000).getTime()
  const endTime = new Date(end.getTime() - 60 * 60 * 1000 * 2).getTime()

  const [buttonPosition, setButtonPosition] = useState<number>(() =>
    calculatePosition(start.getTime(), end.getTime(), startTime),
  )
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMove = (clientX: number) => {
    if (!containerRef.current) {
      return
    }
    const containerRect = containerRef.current.getBoundingClientRect()
    const newLeft =
      dir === 'ltr'
        ? clientX - containerRect.left
        : containerRect.right - clientX
    const containerWidth = containerRect.width

    const totalDuration = endTime - startTime
    const newTime = startTime + (newLeft / containerWidth) * totalDuration

    const stepInMilliseconds = step * 60 * 1000
    const roundedTime =
      Math.round(newTime / stepInMilliseconds) * stepInMilliseconds

    const startBoundary = startTime
    const endBoundary = endTime
    const clampedTime = Math.max(
      startBoundary,
      Math.min(roundedTime, endBoundary),
    )

    const newPosition = calculatePosition(
      start.getTime(),
      end.getTime(),
      clampedTime,
    )

    setButtonPosition(newPosition)
  }

  const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX)

  const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX)

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const handleTouchEnd = () => {
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }

  const handleMouseDown = () => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleTouchStart = () => {
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)
  }

  const s = start.toString()
  const e = end.toString()

  useEffect(() => {
    const selectedTime = calculateTime(new Date(s), new Date(e), buttonPosition)

    onChange(selectedTime)
  }, [buttonPosition, s, e, onChange])

  return {
    containerRef,
    buttonPosition,
    handleMouseDown,
    handleTouchStart,
  }
}
